import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Injectable, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { SelectionModel } from '@angular/cdk/collections';
import { RouteColors } from '@huge-monorepo/hip/domain';
import { MatCheckboxChange as MatCheckboxChange } from '@angular/material/checkbox';

/**
 * File node data with nested structure.
 * Each node has a filename, and a type or a list of children.
 */
export class FileNode {
  id: string;
  children: FileNode[];
  filename: string;
  type: any;
}

/** Flat node with expandable and level information */
export class FileFlatNode {
  constructor(public expandable: boolean, public filename: string, public level: number, public type: any, public id: string) {}
}

/**
 * The file structure tree data in string. The data could be parsed into a Json object
 */
const TREE_DATA = JSON.stringify({
  'Route 1': {
    Name: 'Steers',
  },
  'Route 2': {
    Name: 'Mac D',
  },
  'Route 3': {
    Name: 'Pizza',
  },
});

const DATA_TREE = JSON.stringify([
  //   {
  //     Name : 'Route 1',
  //     children : [{
  //       ID : 1,
  //       Name : 'Mac D',
  //       lat : 122.3,
  //       lng : 115.77
  //     }
  //   ]
  //   },
  //   {
  //     Name : 'Route 2',
  //     children : [{
  //       ID : 2,
  //       Name : 'Takealot',
  //       lat : 122.3,
  //       lng : 115.77,
  //     }]
  //   },
  //  {
  //     Name : 'Route 3',
  //     children : [{
  //       ID : 3,
  //       Name : 'Pizza',
  //       lat : 122.3,
  //       lng : 115.77
  //     }
  //   ]
  //   },
]);

/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable()
export class FileDatabase {
  dataChange = new BehaviorSubject<FileNode[]>([]);
  treeChange = new BehaviorSubject<FileNode[]>([]);
  useData = this.dataChange.asObservable();
  get data(): FileNode[] {
    return this.dataChange.value;
  }
  public dataB = [];
  constructor() {
    this.initialize();
  }

  initialize() {
    // Parse the string to json object.
    //const dataObject = JSON.parse(TREE_DATA);
    const dataObject = JSON.parse(DATA_TREE);
    // Build the tree nodes from Json object. The result is a list of `FileNode` with nested
    //     file node as children.
    const data = this.buildRouteTree(dataObject, 0);

    // Notify the change.
    //this.dataChange.next(data);
    this.dataChange.next(data);
    this.useData.subscribe((data: any) => {});
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `FileNode`.
   */
  buildFileTree(obj: { [key: string]: any }, level: number, parentId: string = '0'): FileNode[] {
    return Object.keys(obj).reduce<FileNode[]>((accumulator, key, idx) => {
      const value = obj[key];
      const node = new FileNode();
      node.filename = typeof value === 'object' ? key : value;
      // node.filename =key; initial state of the filename
      /**
       * Make sure your node has an id so we can properly rearrange the tree during drag'n'drop.
       * By passing parentId to buildFileTree, it constructs a path of indexes which make
       * it possible find the exact sub-array that the node was grabbed from when dropped.
       */
      node.id = `${parentId}/${idx}`;

      if (value != null) {
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1, node.id);
        } else {
          node.type = value;
        }
      }

      return accumulator.concat(node);
    }, []);
  }

  buildRouteTree(obj: any[], parentId: number): FileNode[] {
    let arrayNodes: FileNode[] = [];
    for (let route of obj) {
      const node = new FileNode();
      node.filename = route.Name;
      node.id = parentId + '';
      node.type = 'parent';

      for (let child of route.children) {
        node.children = [];
        const childNode = new FileNode();
        childNode.id = `${parentId}/${child.ID}`;
        childNode.filename = child.Name;
        childNode.type = 'child';
        node.children.push(childNode);
      }

      parentId++;
      arrayNodes.push(node);
    }
    return arrayNodes;
  }
}

@Component({
  selector: 'tools-dragable-tree',
  templateUrl: './dragable-tree.component.html',
  styleUrls: ['./dragable-tree.component.scss'],
  providers: [FileDatabase],
})
export class DragableTreeComponent implements OnInit, OnChanges {
  treeControl: FlatTreeControl<FileFlatNode>;
  treeFlattener: MatTreeFlattener<FileNode, FileFlatNode>;
  dataSource: MatTreeFlatDataSource<FileNode, FileFlatNode>;
  // expansion model tracks expansion state
  expansionModel = new SelectionModel<string>(true);
  dragging = false;
  expandTimeout: any;
  expandDelay = 1000;
  validateDrop = false;

  routeColors: RouteColors;
  colorsArray: Array<string> = [];

  @Output() dragAction: EventEmitter<any> = new EventEmitter();
  @Input() DATA_TREE: any = [];
  constructor(private database: FileDatabase) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel, this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<FileFlatNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['DATA_TREE'].currentValue) {
      this.database.dataChange.subscribe((data: any) => {
        let data_tree = JSON.parse(JSON.stringify(this.DATA_TREE));
        data_tree = this.rebuildRouteTree(data_tree, 0);
        this.rebuildTreeForData(data_tree);
      });
    }

    // database.useData.subscribe((data : any) =>{
    // })
  }
  ngOnInit(): void {
    this.routeColors = new RouteColors();
    this.colorsArray = this.routeColors.getColors();
  }

  rebuildRouteTree(obj: any[], parentId: number): FileNode[] {
    let arrayNodes: FileNode[] = [];
    for (let route of obj) {
      const node = new FileNode();
      node.filename = route.Name;
      node.id = parentId + '';
      node.type = 'parent';
      node.children = [];
      if (route.Children.length > 0) {
        for (let child of route.Children) {
          const childNode = new FileNode();
          childNode.id = `${parentId}/${child.ID}`;
          childNode.filename = child.Name;
          childNode.type = 'child';
          node.children.push(childNode);
        }
      } else {
        node.children = [];
      }

      parentId++;
      arrayNodes.push(node);
    }

    return arrayNodes;
  }

  transformer = (node: FileNode, level: number) => {
    return new FileFlatNode(!!node.children, node.filename, level, node.type, node.id);
  };
  private _getLevel = (node: FileFlatNode) => node.level;
  private _isExpandable = (node: FileFlatNode) => node.expandable;
  private _getChildren = (node: FileNode): Observable<FileNode[]> => observableOf(node.children);
  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  // DRAG AND DROP METHODS

  shouldValidate(event: MatCheckboxChange): void {
    this.validateDrop = event.checked;
  }

  /**
   * This constructs an array of nodes that matches the DOM
   */
  visibleNodes(): FileNode[] {
    const result = [];

    function addExpandedChildren(node: FileNode, expanded: string[]) {
      result.push(node);
      if (expanded.includes(node.id)) {
        node.children.map((child) => addExpandedChildren(child, expanded));
      }
    }
    this.dataSource.data.forEach((node) => {
      addExpandedChildren(node, this.expansionModel.selected);
    });
    return result;
  }

  /**
   * Handle the drop - here we rearrange the data based on the drop event,
   * then rebuild the tree.
   * */
  drop(event: CdkDragDrop<string[]>) {
    // ignore drops outside of the tree
    if (!event.isPointerOverContainer) {
      return;
    }

    // construct a list of visible nodes, this will match the DOM.
    // the cdkDragDrop event.currentIndex jives with visible nodes.
    // it calls rememberExpandedTreeNodes to persist expand state
    const visibleNodes = this.visibleNodes();

    // deep clone the data source so we can mutate it
    const changedData = JSON.parse(JSON.stringify(this.dataSource.data));

    // recursive find function to find siblings of node
    function findNodeSiblings(arr: Array<any>, id: string): Array<any> {
      let result, subResult;
      arr.forEach((item, i) => {
        if (item.id === id) {
          result = arr;
        } else if (item.children) {
          subResult = findNodeSiblings(item.children, id);

          if (subResult) result = subResult;
        }
      });

      return result;
    }

    // determine where to insert the node

    let nodeAtDest = visibleNodes[event.currentIndex];
    if (nodeAtDest.children != undefined) {
      const draggedNode: FileNode = event.item.data;
      //prevent the parent node from being dragged
      if (draggedNode.type == 'parent') {
        return;
      }

      let level: number = parseInt(draggedNode.id.split('/')[0]);
      let draggedNodeSiblings = findNodeSiblings(changedData[level].children, draggedNode.id);
      let removedDraggedNodeIndex = draggedNodeSiblings.findIndex((n) => n.id == draggedNode.id);
      let nodeToInsert: FileNode = draggedNodeSiblings[removedDraggedNodeIndex];

      draggedNodeSiblings = draggedNodeSiblings.filter((n: any) => n.id != draggedNode.id);

      //index to remove the dragged node
      let draggedPosition = nodeToInsert.id.split('/')[0];
      //removed the dragged node to destination
      changedData[draggedPosition].children = JSON.parse(JSON.stringify(draggedNodeSiblings));

      // destination index
      let destinationPosition = nodeAtDest.id.split('/')[0];
      // change the dragged nodes Id to have a level {level}/{node ID}
      nodeToInsert = this.changeIndices(nodeAtDest.id, nodeToInsert);
      changedData[destinationPosition].children.push(nodeToInsert);

      this.rebuildTreeForData(changedData);
      return;
    }

    const newSiblings = findNodeSiblings(changedData, nodeAtDest.id);
    if (!newSiblings) return;
    const insertIndex = newSiblings.findIndex((s) => s.id === nodeAtDest.id);

    // remove the node from its old place
    const node = event.item.data;
    const siblings = findNodeSiblings(changedData, node.id);
    const siblingIndex = siblings.findIndex((n) => n.id === node.id);
    let nodeToInsert: FileNode = siblings.splice(siblingIndex, 1)[0];
    if (nodeToInsert.children) {
      return;
    }
    if (nodeAtDest.id === nodeToInsert.id) {
      return;
    }

    // // ensure validity of drop - must be same level
    // const nodeAtDestFlatNode = this.treeControl.dataNodes.find((n) => nodeAtDest.id === n.id);
    // if (this.validateDrop && nodeAtDestFlatNode.level !== node.level) {
    //   alert('Items can only be moved within the same level.');
    //   return;
    // }

    // insert node
    nodeToInsert = this.changeIndices(nodeAtDest.id, nodeToInsert);
    const nodeInserted = newSiblings.splice(insertIndex, 0, nodeToInsert);

    // // rebuild tree with mutated data
    this.rebuildTreeForData(changedData);
  }

  changeIndices(levelId: string, data: FileNode) {
    let levelArray = levelId.split('/');
    let nodeId = data.id.split('/')[1];
    let newId = levelArray[0] + '/' + nodeId;
    data.id = newId;
    return data;
  }
  /**
   * Experimental - opening tree nodes as you drag over them
   */
  dragStart() {
    this.dragging = true;
  }
  dragEnd() {
    this.dragging = false;
  }
  dragHover(node: FileFlatNode) {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = setTimeout(() => {
        this.treeControl.expand(node);
      }, this.expandDelay);
    }
  }
  dragHoverEnd() {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
    }
  }

  /**
   * The following methods are for persisting the tree expand state
   * after being rebuilt
   */

  rebuildTreeForData(data: any) {
    this.dataSource.data = data;
    this.expansionModel.selected.forEach((id) => {
      const node = this.treeControl.dataNodes.find((n) => n.id === id);
      this.treeControl.expand(node);
    });

    this.outDATA(data);
  }

  outDATA(data: FileNode[]) {
    let dataARRAY = JSON.parse(JSON.stringify(data));
    let locationArray: Array<Array<any>> = new Array<any>(data.length).fill([]);
    dataARRAY.forEach((file: any, index: number) => {
      locationArray[index] = [];
      if (file.children.length > 0) {
        for (let child of file.children) {
          let extractId = child.id.split('/')[1];
          child.id = extractId;
          locationArray[index].push(child);
        }
      }
    });
    this.dragAction.emit(locationArray);
  }

  /**
   * Not used but you might need this to programmatically expand nodes
   * to reveal a particular node
   */
  private expandNodesById(flatNodes: FileFlatNode[], ids: string[]) {
    if (!flatNodes || flatNodes.length === 0) return;
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.id)) {
        this.treeControl.expand(node);
        let parent = this.getParentNode(node);
        while (parent) {
          this.treeControl.expand(parent);
          parent = this.getParentNode(parent);
        }
      }
    });
  }

  private getParentNode(node: FileFlatNode): FileFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  colorView(node: any) {
    let colorContainer: any = document.getElementsByClassName(`color-${node}`)[0];
    if (colorContainer) {
      let index = this.DATA_TREE.findIndex((data: any) => data.Name == node);
      colorContainer.style.backgroundColor = this.colorsArray[index];
      colorContainer.style.width = '15px';
      colorContainer.style.height = '15px';
      colorContainer.style.marginLeft = '16px';
    }

    return true;
  }
}
